import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = '/api';

const TIMEZONES = [
  'UTC','Asia/Dubai','Asia/Riyadh','Asia/Kuwait','Asia/Bahrain','Asia/Muscat',
  'Asia/Karachi','Asia/Kolkata','Europe/London','Europe/Berlin',
  'America/New_York','America/Chicago','America/Los_Angeles',
  'Asia/Singapore','Asia/Tokyo','Australia/Sydney',
];

const PLANS = ['standard','professional','enterprise'];

interface TenantSettings {
  id: number; tenant_name: string; company_name?: string;
  domain_name?: string; contact_email?: string;
  max_users: number; max_queues: number; extension_base: number;
  timezone: string; plan: string; is_active: number;
}

interface PauseReason {
  id: number; reason_code: string; reason_name: string; is_active: number; tenant_id: number;
}

export default function SettingsPage({ user, tenants }: { user: any; tenants?: any[] }) {
  const isSuperAdmin = user?.role === 'super_admin' || !!user?.is_admin;
  const [tab, setTab] = useState<'tenant' | 'pause' | 'platform'>('tenant');

  // ── Tenant Settings ────────────────────────────────────────────────────────
  const [selTenant, setSelTenant] = useState<number>(user?.tenant_id || 0);
  const [tSettings, setTSettings] = useState<TenantSettings | null>(null);
  const [tForm, setTForm] = useState<Partial<TenantSettings>>({});
  const [tLoading, setTLoading] = useState(false);
  const [tSaving, setTSaving] = useState(false);
  const [tError, setTError] = useState('');
  const [tSuccess, setTSuccess] = useState('');

  const loadTenantSettings = useCallback(async (tid: number) => {
    if (!tid) return;
    setTLoading(true); setTError('');
    try {
      const r = await axios.get(`${API}/admin/tenants/${tid}/settings`);
      const d = r.data;
      setTSettings(d);
      setTForm({
        company_name: d.company_name || '',
        domain_name: d.domain_name || '',
        contact_email: d.contact_email || '',
        max_users: d.max_users || 50,
        max_queues: d.max_queues || 10,
        extension_base: d.extension_base || 1000,
        timezone: d.timezone || 'UTC',
        plan: d.plan || 'standard',
      });
    } catch { setTError('Failed to load tenant settings'); }
    setTLoading(false);
  }, []);

  useEffect(() => {
    if (tab === 'tenant') {
      const tid = isSuperAdmin && tenants?.length ? (selTenant || tenants[0]?.id) : user?.tenant_id;
      if (tid) { setSelTenant(tid); loadTenantSettings(tid); }
    }
  }, [tab]); // eslint-disable-line

  const saveTenantSettings = async () => {
    if (!selTenant) return;
    setTSaving(true); setTError(''); setTSuccess('');
    try {
      await axios.put(`${API}/admin/tenants/${selTenant}/settings`, tForm);
      setTSuccess('Settings saved successfully');
      setTimeout(() => setTSuccess(''), 3000);
    } catch (e: any) { setTError(e?.response?.data?.error || 'Save failed'); }
    setTSaving(false);
  };

  // ── Pause Reasons ──────────────────────────────────────────────────────────
  const [pauseReasons, setPauseReasons] = useState<PauseReason[]>([]);
  const [prLoading, setPrLoading] = useState(false);
  const [prNew, setPrNew] = useState('');
  const [prSaving, setPrSaving] = useState(false);
  const [prError, setPrError] = useState('');
  const [prSuccess, setPrSuccess] = useState('');

  const loadPauseReasons = useCallback(async () => {
    setPrLoading(true);
    try {
      const r = await axios.get(`${API}/admin/pause-reasons`);
      setPauseReasons(Array.isArray(r.data) ? r.data : []);
    } catch { setPrError('Failed to load pause reasons'); }
    setPrLoading(false);
  }, []);

  useEffect(() => { if (tab === 'pause') loadPauseReasons(); }, [tab, loadPauseReasons]);

  const addPauseReason = async () => {
    if (!prNew.trim()) return;
    setPrSaving(true); setPrError(''); setPrSuccess('');
    try {
      await axios.post(`${API}/admin/pause-reasons`, { reason_name: prNew.trim() });
      setPrNew('');
      setPrSuccess('Pause reason added');
      await loadPauseReasons();
    } catch (e: any) { setPrError(e?.response?.data?.error || 'Failed to add'); }
    setPrSaving(false);
  };

  const togglePauseReason = async (pr: PauseReason) => {
    try {
      await axios.put(`${API}/admin/pause-reasons/${pr.id}`, { is_active: pr.is_active ? 0 : 1 });
      await loadPauseReasons();
    } catch { setPrError('Update failed'); }
  };

  const deletePauseReason = async (id: number) => {
    try {
      await axios.delete(`${API}/admin/pause-reasons/${id}`);
      setPauseReasons(p => p.filter(r => r.id !== id));
    } catch { setPrError('Delete failed'); }
  };

  const tabStyle = (t: string) => ({
    padding: '8px 20px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600,
    fontSize: 13, transition: 'all 0.15s',
    background: tab === t ? '#4f46e5' : 'transparent',
    color: tab === t ? '#fff' : '#9ca3af',
  });

  const fieldStyle: React.CSSProperties = {
    padding: '8px 12px', background: '#1e2538', border: '1px solid #374151',
    borderRadius: 8, color: '#f9fafb', fontSize: 13, width: '100%', boxSizing: 'border-box',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: 12, color: '#9ca3af', marginBottom: 4, fontWeight: 500,
  };

  return (
    <>
      <div className="admin-page-title">Settings</div>
      <div className="admin-page-subtitle">
        Tenant configuration, agent pause reasons, and platform information
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 24, background: '#1e2538', borderRadius: 10, padding: 4, width: 'fit-content' }}>
        <button style={tabStyle('tenant')} onClick={() => setTab('tenant')}>Tenant Settings</button>
        <button style={tabStyle('pause')} onClick={() => setTab('pause')}>Pause Reasons</button>
        {isSuperAdmin && (
          <button style={tabStyle('platform')} onClick={() => setTab('platform')}>Platform Info</button>
        )}
      </div>

      {/* ── Tenant Settings ── */}
      {tab === 'tenant' && (
        <div className="content-card">
          <h3 style={{ marginTop: 0 }}>Tenant Configuration</h3>

          {isSuperAdmin && tenants && tenants.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Select Tenant</label>
              <select
                value={selTenant}
                onChange={e => {
                  const tid = Number(e.target.value);
                  setSelTenant(tid);
                  loadTenantSettings(tid);
                }}
                style={{ ...fieldStyle, width: 360 }}
              >
                {tenants.map((t: any) => (
                  <option key={t.id} value={t.id}>{t.tenant_name} — {t.company_name}</option>
                ))}
              </select>
            </div>
          )}

          {tError && <div style={{ color: '#ef4444', marginBottom: 12, fontSize: 13 }}>{tError}</div>}
          {tSuccess && <div style={{ color: '#10b981', marginBottom: 12, fontSize: 13 }}>✓ {tSuccess}</div>}

          {tLoading ? (
            <div style={{ color: '#9ca3af', textAlign: 'center', padding: 32 }}>Loading…</div>
          ) : tSettings ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, maxWidth: 720 }}>
                <div>
                  <label style={labelStyle}>Company Name</label>
                  <input value={tForm.company_name ?? ''} onChange={e => setTForm(f => ({ ...f, company_name: e.target.value }))} style={fieldStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Contact Email</label>
                  <input type="email" value={tForm.contact_email ?? ''} onChange={e => setTForm(f => ({ ...f, contact_email: e.target.value }))} style={fieldStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Domain / Subdomain</label>
                  <input value={tForm.domain_name ?? ''} onChange={e => setTForm(f => ({ ...f, domain_name: e.target.value }))} style={fieldStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Timezone</label>
                  <select value={tForm.timezone ?? 'UTC'} onChange={e => setTForm(f => ({ ...f, timezone: e.target.value }))} style={fieldStyle}>
                    {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                  </select>
                </div>
                {isSuperAdmin && (
                  <>
                    <div>
                      <label style={labelStyle}>Plan</label>
                      <select value={tForm.plan ?? 'standard'} onChange={e => setTForm(f => ({ ...f, plan: e.target.value }))} style={fieldStyle}>
                        {PLANS.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>Max Users</label>
                      <input type="number" min={1} value={tForm.max_users ?? 50} onChange={e => setTForm(f => ({ ...f, max_users: Number(e.target.value) }))} style={fieldStyle} />
                    </div>
                    <div>
                      <label style={labelStyle}>Max Queues</label>
                      <input type="number" min={1} value={tForm.max_queues ?? 10} onChange={e => setTForm(f => ({ ...f, max_queues: Number(e.target.value) }))} style={fieldStyle} />
                    </div>
                    <div>
                      <label style={labelStyle}>Extension Base</label>
                      <input type="number" min={100} value={tForm.extension_base ?? 1000} onChange={e => setTForm(f => ({ ...f, extension_base: Number(e.target.value) }))} style={fieldStyle} />
                    </div>
                  </>
                )}
              </div>
              <div style={{ maxWidth: 720 }}>
                <div style={{ padding: 12, background: '#0f172a', borderRadius: 8, margin: '16px 0', fontSize: 12, color: '#6b7280' }}>
                  <span style={{ color: '#94a3b8' }}>Tenant ID:</span> {tSettings.id} &nbsp;|&nbsp;
                  <span style={{ color: '#94a3b8' }}>Slug:</span> {tSettings.tenant_name} &nbsp;|&nbsp;
                  <span style={{ color: '#94a3b8' }}>Status:</span>{' '}
                  <span style={{ color: tSettings.is_active ? '#10b981' : '#ef4444' }}>{tSettings.is_active ? 'Active' : 'Suspended'}</span>
                </div>
                <button
                  onClick={saveTenantSettings}
                  disabled={tSaving}
                  style={{ padding: '10px 28px', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}
                >
                  {tSaving ? 'Saving…' : 'Save Settings'}
                </button>
              </div>
            </>
          ) : (
            <div style={{ color: '#6b7280', textAlign: 'center', padding: 40 }}>
              {isSuperAdmin ? 'Select a tenant to view its settings.' : 'No settings available.'}
            </div>
          )}
        </div>
      )}

      {/* ── Pause Reasons ── */}
      {tab === 'pause' && (
        <div className="content-card">
          <h3 style={{ marginTop: 0 }}>Agent Pause Reasons</h3>
          <p style={{ color: '#9ca3af', fontSize: 13 }}>
            Define the pause states agents can select when stepping away from the queue (Lunch Break, Training, Meeting, etc.)
          </p>

          {prError && <div style={{ color: '#ef4444', marginBottom: 10, fontSize: 13 }}>{prError}</div>}
          {prSuccess && <div style={{ color: '#10b981', marginBottom: 10, fontSize: 13 }}>✓ {prSuccess}</div>}

          <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
            <input
              value={prNew}
              onChange={e => setPrNew(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addPauseReason()}
              placeholder="New pause reason (e.g. Lunch Break, Training, Meeting)"
              style={{ flex: 1, padding: '8px 12px', background: '#1e2538', border: '1px solid #374151', borderRadius: 8, color: '#f9fafb', fontSize: 13 }}
            />
            <button
              onClick={addPauseReason}
              disabled={prSaving || !prNew.trim()}
              style={{ padding: '8px 20px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap' }}
            >
              {prSaving ? 'Adding…' : '+ Add Reason'}
            </button>
          </div>

          {prLoading ? (
            <div style={{ color: '#9ca3af', textAlign: 'center', padding: 32 }}>Loading…</div>
          ) : pauseReasons.length === 0 ? (
            <div style={{ color: '#6b7280', textAlign: 'center', padding: 40 }}>No pause reasons configured. Add one above.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {pauseReasons.map(pr => (
                <div key={pr.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: '#0f172a', borderRadius: 8 }}>
                  <span style={{ fontSize: 18 }}>{pr.is_active ? '⏸' : '⏭'}</span>
                  <span style={{ flex: 1, fontSize: 14, color: pr.is_active ? '#f9fafb' : '#6b7280', textDecoration: pr.is_active ? 'none' : 'line-through' }}>
                    {pr.reason_name}
                    <span style={{ marginLeft: 8, fontSize: 11, color: '#4b5563', fontFamily: 'monospace' }}>({pr.reason_code})</span>
                  </span>
                  <button
                    onClick={() => togglePauseReason(pr)}
                    style={{
                      padding: '4px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                      background: pr.is_active ? '#10b98122' : '#6b728022',
                      color: pr.is_active ? '#10b981' : '#6b7280',
                    }}
                  >
                    {pr.is_active ? 'Enabled' : 'Disabled'}
                  </button>
                  <button
                    onClick={() => deletePauseReason(pr.id)}
                    style={{ padding: '4px 10px', background: 'transparent', color: '#ef4444', border: '1px solid #ef444444', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Platform Info (super admin only) ── */}
      {tab === 'platform' && isSuperAdmin && (
        <div className="content-card">
          <h3 style={{ marginTop: 0 }}>Platform Information</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, maxWidth: 700, marginBottom: 24 }}>
            {([
              ['Platform', 'CallEssential by Synergytalk'],
              ['Server', 'dev.callessential.cc'],
              ['IP Address', '151.253.188.4'],
              ['OS', 'Ubuntu 24.04 LTS'],
              ['Telephony Engine', 'CE-PBX v22'],
              ['API Backend', 'Node.js / Express (PM2)'],
              ['AI Backend', 'Python FastAPI — port 8001'],
              ['Database', 'PostgreSQL (callessential_db)'],
              ['Demo VoiceBot', 'FastAGI — port 4574 (PM2)'],
              ['WebRTC', 'coturn STUN/TURN'],
            ] as const).map(([k, v]) => (
              <div key={k} style={{ padding: '12px 14px', background: '#0f172a', borderRadius: 8 }}>
                <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>{k}</div>
                <div style={{ fontSize: 13, color: '#e2e8f0', fontFamily: ['IP Address', 'Server'].includes(k as string) ? 'monospace' : undefined }}>{v}</div>
              </div>
            ))}
          </div>

          <h4 style={{ color: '#94a3b8', marginBottom: 10 }}>Service URLs</h4>
          <div style={{ background: '#0f172a', borderRadius: 8, padding: 16, fontFamily: 'monospace', fontSize: 12, lineHeight: 2 }}>
            {[
              ['API', '/api'],
              ['WebSocket', 'wss://' + window.location.host + '/ws'],
              ['AI Module', 'http://127.0.0.1:8001'],
              ['Demo FastAGI', 'agi://127.0.0.1:4574'],
              ['Engine Interface', 'Internal'],
            ].map(([label, url]) => (
              <div key={label}>
                <span style={{ color: '#6b7280', minWidth: 130, display: 'inline-block' }}>{label}:</span>
                <span style={{ color: '#60a5fa' }}>{url}</span>
              </div>
            ))}
          </div>

          <h4 style={{ color: '#94a3b8', marginBottom: 10, marginTop: 20 }}>Default Passwords</h4>
          <div style={{ background: '#0f172a', borderRadius: 8, padding: 16, fontSize: 13, lineHeight: 2 }}>
            <div><span style={{ color: '#fbbf24', minWidth: 240, display: 'inline-block' }}>Super Admin / Tenant Admin:</span><code style={{ color: '#86efac' }}>P@ssw0rd@786try</code></div>
            <div><span style={{ color: '#fbbf24', minWidth: 240, display: 'inline-block' }}>PBX Users / Agents / Supervisors:</span><code style={{ color: '#86efac' }}>P@ssw0rd@786</code></div>
          </div>
        </div>
      )}
    </>
  );
}
